import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Alert, Button, Card, Empty, Input, Spin } from 'antd';
import { QrcodeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SortableList from '../../dnd/SortableList';
import SortableItem from '../../dnd/SortableItem';
import DragNDropHelpers from '../../../services/helpers/DragNDropHelpers';
import useElementWidth from '../../../hooks/utility/useElementWidth';
import AddTableForm from './AddTableForm';
import DeleteTableConfirm from './DeleteTableConfirm';
import TableQrRenderer from './TableQrRenderer';
import TableRow from './TableRow';
import {
  SEARCH_FROM,
  TableListLayout,
  cleanTableName,
  filterTables,
  layoutForWidth,
  moveTable,
  normaliseTableName,
  reorderAnnouncement,
} from './tableList.utils';

/**
 * The tables card of Order Methods → Table ordering (PROD-724): add a table, then the list. Each
 * table has a permanent link and QR code. Changes save right away through the callbacks
 * (`useOnSiteTables`), which confirm with a toast. From 15 tables a search field and the count show,
 * and the list scrolls in its own box. The layout follows the card's own width. A list that didn't
 * load says so, with Try again, instead of looking empty.
 */
export default function TableList({
  tables,
  loading = false,
  failed = false,
  onRetry,
  restaurantName,
  showMigratedNote = false,
  onAdd,
  onRename,
  onToggle,
  onDelete,
  onReorder,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const layout = layoutForWidth(useElementWidth(containerRef));

  const [search, setSearch] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [announcement, setAnnouncement] = useState({ key: 0, text: '' });
  // Where focus goes once the list has re-rendered: the first of these selectors that matches an
  // enabled element. Keeps keyboard users in place after a move, a rename or a delete.
  const [focusRequest, setFocusRequest] = useState(null);
  const deleteFocus = useRef(null);
  const renamingRef = useRef(renamingId);
  renamingRef.current = renamingId;

  const count = tables.length;
  const long = count >= SEARCH_FROM;
  const query = long ? search : '';
  const filtering = Boolean(normaliseTableName(query));
  const shown = useMemo(() => filterTables(tables, query), [tables, query]);
  const scrolls = long && layout !== TableListLayout.Phone;

  const focusFirst = (selectors) => {
    const target = selectors
      .map((selector) => containerRef.current?.querySelector(selector))
      .find((element) => element && !element.disabled);

    target?.focus();
  };

  useEffect(() => {
    if (!focusRequest) {
      return;
    }

    focusFirst(focusRequest);
    setFocusRequest(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, focusRequest]);

  const announce = (list, id) =>
    setAnnouncement((current) => ({ key: current.key + 1, text: reorderAnnouncement(t, list, id) }));

  const move = (table, delta) => {
    const from = tables.findIndex((item) => item.id === table.id);
    const next = moveTable(tables, from, from + delta);

    if (next === tables) {
      return;
    }

    onReorder(next);
    announce(next, table.id);
    // A row moved with the arrow keys keeps the focus on its handle.
    setFocusRequest([handleSelector(table.id)]);
  };

  const onDragEnd = (event) =>
    DragNDropHelpers.onDndKitDragEnd(
      tables,
      event,
      (next) => {
        onReorder(next);
        announce(next, event.active.id);
      },
      (item) => item.id,
    );

  const downloadQr = async (table) => {
    const saved = await qrRef.current?.download(table);

    // null: another image is still being made; the click is ignored.
    if (saved === false) {
      toast.error(t('toasts.failed_to_download_qr'));
    }
  };

  // A table added while a search is on would be filtered out of sight: the search goes.
  const add = async (name) => {
    const error = await onAdd(name);

    if (!error) {
      setSearch('');
    }

    return error;
  };

  // After a delete, focus goes to the next row (or the one before, or the add field when none is
  // left), not to the body: the button that opened the dialog is gone with its row.
  const confirmDelete = async (table) => {
    const index = shown.findIndex((item) => item.id === table.id);
    const neighbour = shown[index + 1] ?? shown[index - 1];

    if (await onDelete(table)) {
      deleteFocus.current = neighbour ? rowFocusSelectors(neighbour.id) : [ADD_FIELD_SELECTOR];
      setFocusRequest(deleteFocus.current);
      setDeleting(null);
    }
  };

  // The dialog hands focus back to its opener once it has closed; after a delete that opener is
  // gone, so the row picked above gets it (again) here.
  const afterDeleteClosed = () => {
    if (deleteFocus.current) {
      focusFirst(deleteFocus.current);
      deleteFocus.current = null;
    }
  };

  // Leaving a rename puts focus back on the row's Rename (or ⋮) button. A rename that finishes after
  // another row's started leaves that row's field alone.
  const stopRename = (table) => {
    if (renamingRef.current !== table.id) {
      return;
    }

    setRenamingId(null);
    setFocusRequest([`[data-table-rename="${table.id}"]`]);
  };

  const renderRow = (table, element) => (
    <SortableItem
      key={table.id}
      id={table.id}
      as={element}
      className={element === 'tr' ? 'bg-white' : undefined}
      data-testid={element === 'tr' ? 'table-row' : undefined}
      disabled={filtering || renamingId === table.id}
    >
      {({ dragHandleProps }) => (
        <TableRow
          table={table}
          tables={tables}
          layout={layout}
          dragHandleProps={dragHandleProps}
          reorderDisabled={filtering}
          renaming={renamingId === table.id}
          onStartRename={(item) => setRenamingId(item.id)}
          onStopRename={() => stopRename(table)}
          onRename={onRename}
          onToggle={onToggle}
          onDelete={setDeleting}
          onDownloadQr={downloadQr}
          onMove={move}
        />
      )}
    </SortableItem>
  );

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-lg">
          <Spin />
        </div>
      );
    }

    if (failed) {
      return (
        <Alert
          type="error"
          showIcon
          title={t('load_failed.title')}
          action={
            <Button size="small" onClick={onRetry}>
              {t('load_failed.retry')}
            </Button>
          }
          data-testid="table-list-load-failed"
        />
      );
    }

    if (!count) {
      return (
        <Empty
          image={<QrcodeOutlined className="text-[40px] text-gray-400" />}
          styles={{ image: { height: 48 } }}
          description={
            <div data-testid="table-list-empty">
              <div className="font-semibold text-gray-900">{t('empty.title')}</div>
              <div className="mt-2xs text-gray-500">{t('empty.text')}</div>
            </div>
          }
          className="my-md"
        />
      );
    }

    return (
      <div className="flex flex-col gap-sm">
        {long && (
          <div className="flex flex-wrap items-center gap-sm">
            <Input.Search
              allowClear
              className="max-w-[320px]"
              placeholder={t('search.placeholder')}
              aria-label={t('search.placeholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className="text-gray-500" aria-live="polite" data-testid="table-list-count">
              {filtering ? t('filtered_count', { shown: shown.length, count }) : t('count', { count })}
            </span>
          </div>
        )}
        <div
          className={classnames(
            'rounded-md border border-gray-200',
            scrolls ? 'max-h-[640px] overflow-y-auto' : 'overflow-hidden',
          )}
          data-testid="table-list-box"
        >
          <SortableList
            items={shown.map((table) => table.id)}
            onDragEnd={onDragEnd}
            accessibility={{ screenReaderInstructions: { draggable: t('announcements.instructions') } }}
          >
            {layout === TableListLayout.Wide ? (
              <table
                className="w-full border-separate border-spacing-0 [&_tbody_tr:last-child_td]:border-b-0"
                data-testid="table-list-table"
              >
                <thead>
                  <tr>
                    {[
                      ['order', true],
                      ['table', false],
                      ['on', false],
                      ['copy', true],
                      ['qr', true],
                      ['actions', true],
                    ].map(([key, hidden]) => (
                      <th
                        key={key}
                        scope="col"
                        className={classnames(
                          'border-b border-gray-200 bg-gray-50 px-xs py-2xs text-left text-sm font-semibold text-gray-600',
                          { 'sticky top-0 z-10': scrolls },
                        )}
                      >
                        <span className={hidden ? 'sr-only' : undefined}>{t(`columns.${key}`)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{shown.map((table) => renderRow(table, 'tr'))}</tbody>
              </table>
            ) : (
              shown.map((table) => renderRow(table, 'div'))
            )}
          </SortableList>
          {filtering && !shown.length && (
            <p className="m-0 p-md text-gray-500" data-testid="table-list-no-results">
              {t('search.no_results', { text: cleanTableName(query) })}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="mb-md">
      <div ref={containerRef} className="flex flex-col gap-md" data-testid="table-list" data-layout={layout}>
        <div className="flex items-baseline justify-between gap-md">
          <div className="text-base font-semibold">{t('title')}</div>
          {count > 0 && !long && <span className="text-gray-500">{t('count', { count })}</span>}
        </div>
        <div className="-mt-xs max-w-[640px] text-gray-500">{t('intro')}</div>
        {showMigratedNote && (
          <Alert
            type="info"
            showIcon
            title={t('migrated.title')}
            description={t('migrated.description')}
            data-testid="table-list-migrated-note"
          />
        )}
        {!failed && <AddTableForm tables={tables} onAdd={add} />}
        {renderList()}
        <p key={announcement.key} className="sr-only" aria-live="polite" data-testid="table-list-announcement">
          {announcement.text}
        </p>
      </div>
      <DeleteTableConfirm
        table={deleting}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
        afterClose={afterDeleteClosed}
      />
      <TableQrRenderer ref={qrRef} restaurantName={restaurantName} tableLabel={t('qr_image.table')} />
    </Card>
  );
}

const ADD_FIELD_SELECTOR = '#table-ordering-add-name';

function handleSelector(id) {
  return `[data-table-handle="${id}"]`;
}

/** A row's handle, or its on/off switch while the handles are off (searching). */
function rowFocusSelectors(id) {
  return [handleSelector(id), `[data-table-switch="${id}"]`];
}
